import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const PRIORITY_COLORS = {
  low: "text-zinc-500",
  medium: "text-blue-400",
  high: "text-amber-400",
  urgent: "text-red-400",
} as const;

export const PRIORITY_DOT_COLORS = {
  low: "bg-zinc-600",
  medium: "bg-blue-500",
  high: "bg-amber-500",
  urgent: "bg-red-500",
} as const;

export const TASK_STATUS_ORDER = ["inbox", "ready", "working", "decision", "review", "done"] as const;

export const TASK_STATUS_LABELS = {
  inbox: "Inbox",
  ready: "Ready",
  working: "Working",
  decision: "Needs Decision",
  review: "Review",
  done: "Done",
} as const;

export const TASK_STATUS_HELP = {
  inbox: "Needs triage or clarification.",
  ready: "Approved and waiting for someone to pick it up.",
  working: "Actively being worked right now.",
  decision: "Waiting for a human decision before the agent can continue.",
  review: "Work is done and waiting for review.",
  done: "Completed and closed.",
} as const;

export const STATUS_COLORS = {
  inbox: "bg-zinc-800 text-zinc-300",
  ready: "bg-blue-950 text-blue-300",
  working: "bg-indigo-950 text-indigo-300",
  decision: "bg-amber-950 text-amber-300",
  review: "bg-amber-950 text-amber-300",
  done: "bg-emerald-950 text-emerald-300",
} as const;

export const AGENT_STATUS_COLORS = {
  active: "bg-emerald-500",
  idle: "bg-zinc-500",
  blocked: "bg-red-500",
  waiting: "bg-amber-500",
  inactive: "bg-zinc-800",
} as const;

export const AGENT_STATUS_LABELS = {
  active: "Active",
  idle: "Idle",
  blocked: "Blocked",
  waiting: "Waiting",
  inactive: "Inactive",
} as const;

export function isStale(updatedAt: number, status: string): boolean {
  if (status !== "working") return false;
  const threeDaysAgo = Date.now() - 3 * 86400000;
  return updatedAt < threeDaysAgo;
}

export function formatRelativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function formatDate(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatCron(expr: string): string {
  const parts = expr.split(" ");
  if (parts.length !== 5) return expr;
  const [min, hour, , , dow] = parts;

  if (dow === "1-5") {
    return `Weekdays at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "1") {
    return `Every Monday at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "3") {
    return `Every Wednesday at ${hour}:${min.padStart(2, "0")}`;
  }
  if (dow === "5") {
    return `Every Friday at ${hour}:${min.padStart(2, "0")}`;
  }
  return expr;
}
